import { useState, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import PlayButton from "public/assets/play.svg"
import cn from "classnames"
import Head from "next/head"

export default function DynamicVideo({ component, ...videoFields }) {
  switch (component) {
    case "video_embed":
      return <VideoEmbed {...videoFields} />
    case "yt_embed":
      return <YTEmbed {...videoFields} />
    default:
      return "Invalid video type"
  }
}

function VideoEmbed({ video_asset, thumbnail }) {
  const [videoPlaying, setVideoPlaying] = useState(false)
  const videoRef = useRef(null)

  let type = `video/${video_asset.filename.match(/.(mov|mp4)/gi)[0]}`.replace(".", "")
  switch (type) {
    case "video/mov":
      type = "video/mp4"
      break
    case "video/mp4":
      type = "video/mp4"
      break
    default:
      type = "invalid"
      break
  }

  return (
    <div className="relative mx-auto">
      <button
        onClick={() => {
          setVideoPlaying(!videoPlaying)
          videoPlaying === true ? videoRef.current.pause() : videoRef.current.play()
        }}
        className={cn("absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 z-10", {
          hidden: videoPlaying,
        })}
        title="Play video"
      >
        <PlayButton className="play-video carousel-arrow" />
      </button>
      <video
        ref={videoRef}
        poster={thumbnail.filename}
        src={video_asset.filename}
        className="video-embed w-full aspect-video"
        type={type}
        controls
        onPlay={() => setVideoPlaying(true)}
        onPause={() => setVideoPlaying(false)}
      />
    </div>
  )
}

function YTEmbed({ id, thumbnail }) {
  const [videoPlay, setVideoPlay] = useState(false)
  const [preconnected, setPreconnected] = useState(false)

  const YTVideoStyles = cn("aspect-video cursor-pointer w-full", { "pointer-events-none object-cover": !videoPlay })
  const layoutId = `video-${id}`

  const thumbnailURL = thumbnail?.filename ?? `https://img.youtube.com/vi/${id}/hqdefault.jpg`

  return (
    <>
      <Head>
        <link key="youtube-thumbnails" rel="preconnect" href="https://img.youtube.com" />
      </Head>
      <AnimatePresence mode="wait">
        <motion.div
          layoutId={layoutId}
          initial={{ x: 0, y: 50, opacity: 0.5, scale: 1 }}
          animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          exit={{ x: 0, y: 500, scale: 0 }}
          viewport={{ once: true }}
        >
          {preconnected ? (
            <Head>
              {/* Keys must be present to avoid duplicate tag creation */}
              <link key="youtube-nocookie" rel="preconnect" href="https://www.youtube-nocookie.com" />
              <link key="google" rel="preconnect" href="https://www.google.com" />
              <link key="googleads" rel="preconnect" href="https://googleads.g.doubleclick.net" />
              <link key="doubleclick" rel="preconnect" href="https://static.doubleclick.net" />
            </Head>
          ) : null}
          {!videoPlay ? (
            <>
              <div
                onMouseEnter={() => setPreconnected(true)}
                className="youtube-thumbnail relative cursor-pointer z-10"
                onClick={() => setVideoPlay(true)}
              >
                <img src={thumbnailURL} alt="YouTube Video" className={YTVideoStyles} />
                <PlayButton
                  className="play-btn carousel-arrow absolute scale-50 xs:scale-100 top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer"
                  title="Play video"
                />
              </div>
              <button className="sr-only" onClick={() => setVideoPlay(true)}>
                Load YouTube Video
              </button>
            </>
          ) : (
            <iframe
              className={cn(YTVideoStyles, "youtube-iframe")}
              src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=0&playsinline=1&modestbranding=1&rel=0&disablekb=1`}
              allowFullScreen
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            />
          )}
        </motion.div>
      </AnimatePresence>
    </>
  )
}
